import React, {useState} from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Fab,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import {FeedbackRestControllerApiFactory} from "arteco-api-client-ts";
import {useApiManager} from "../utils/api";
import {btnSx} from "../kit";

/**
 * Botón discreto abajo a la derecha para avisar de un problema en la página (#13892). Abre un diálogo
 * con un texto libre; se envía también la dirección, el navegador y el tamaño de la ventana para poder
 * reproducirlo. Cada aviso es una incidencia en el backlog de la Extranet y un correo a dirección.
 */
export const FeedbackButton = () => {
	const apiManager = useApiManager();
	const [open, setOpen] = useState(false);
	const [message, setMessage] = useState("");
	const [sending, setSending] = useState(false);
	const [sent, setSent] = useState<{ issueId?: number }>();
	const [error, setError] = useState(false);

	const close = () => {
		setOpen(false);
		// Se limpia al cerrar del todo para que no se vea el cambio durante la animación.
		setTimeout(() => {
			setMessage("");
			setSent(undefined);
			setError(false);
		}, 200);
	};

	const send = () => {
		setSending(true);
		setError(false);
		apiManager.executeRaw(apiManager.factory(FeedbackRestControllerApiFactory).sendFeedback({
			message: message.trim(),
			url: window.location.href,
			userAgent: navigator.userAgent,
			viewport: `${window.innerWidth}x${window.innerHeight}`,
		}), undefined, true)
			.then(r => setSent({issueId: r.data.issueId}))
			.catch(() => setError(true))
			.finally(() => setSending(false));
	};

	return (
		<>
			<Tooltip title="Avisar de un problema" placement="left">
				<Fab size="small" aria-label="Avisar de un problema" onClick={() => setOpen(true)}
					 sx={{
						 position: "fixed", right: 16, bottom: {xs: 76, md: 16}, zIndex: 1100,
						 bgcolor: "background.paper", color: "text.secondary", boxShadow: "0 1px 4px rgba(16, 24, 40, 0.18)",
						 border: "1px solid", borderColor: "divider", opacity: 0.85,
						 "&:hover": {bgcolor: "background.paper", color: "primary.main", opacity: 1},
						 "@media print": {display: "none"},
					 }}>
					<FeedbackOutlinedIcon fontSize="small"/>
				</Fab>
			</Tooltip>

			<Dialog open={open} onClose={close} fullWidth maxWidth="sm" PaperProps={{sx: {borderRadius: 3}}}>
				{sent ? (
					<>
						<DialogContent sx={{pt: 4, textAlign: "center"}}>
							<CheckCircleRoundedIcon color="success" sx={{fontSize: 48}}/>
							<Typography variant="h6" fontWeight={700} sx={{mt: 1}}>Recibido, gracias</Typography>
							<Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
								Lo revisamos y te decimos algo si hace falta.
								{sent.issueId ? ` Referencia: #${sent.issueId}.` : ""}
							</Typography>
						</DialogContent>
						<DialogActions sx={{px: 3, pb: 2.5, justifyContent: "center"}}>
							<Button onClick={close} variant="contained" sx={btnSx}>Cerrar</Button>
						</DialogActions>
					</>
				) : (
					<>
						<DialogTitle sx={{fontWeight: 700, pb: 0.5}}>Avisar de un problema</DialogTitle>
						<DialogContent>
							<Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
								Cuéntanos qué intentabas hacer y qué ha pasado. Se envía también la dirección de esta
								página, tu usuario y tu navegador, para poder reproducirlo.
							</Typography>
							<TextField autoFocus fullWidth multiline minRows={4} value={message}
									   placeholder="Por ejemplo: al subir un documento, el desplegable «Tipo» no se puede elegir."
									   onChange={e => setMessage(e.target.value)}
									   inputProps={{maxLength: 4000}}/>
							{error && (
								<Alert severity="error" sx={{mt: 2, borderRadius: 2}}>
									No se ha podido enviar. Vuelve a intentarlo o escribe a rrhh@arteco-consulting.com.
								</Alert>
							)}
							<Box sx={{mt: 1, textAlign: "right"}}>
								<Typography variant="caption" color="text.disabled">{message.length}/4000</Typography>
							</Box>
						</DialogContent>
						<DialogActions sx={{px: 3, pb: 2.5}}>
							<Button onClick={close} sx={btnSx}>Cancelar</Button>
							<Button variant="contained" onClick={send} disabled={sending || message.trim().length < 3}
									sx={{...btnSx, px: 3}}>
								{sending ? <CircularProgress size={20} color="inherit"/> : "Enviar aviso"}
							</Button>
						</DialogActions>
					</>
				)}
			</Dialog>
		</>
	);
};
